/**
 * ZippyAssist Analytics Utility
 * Conversion funnels, user journey tracking, and marketplace performance metrics
 */

import { trackEvent } from '../hooks/useGoogleAnalytics';

// ─── CONVERSION FUNNEL STAGES ────────────────────────────────────────────────
// Funnel: Home → Browse → Service Detail → Enquiry → Lead Submitted
export const FUNNEL_STAGES = {
  HOME_VISIT: 'funnel_home_visit',
  CATEGORY_BROWSE: 'funnel_category_browse',
  SERVICE_VIEW: 'funnel_service_view',
  ENQUIRY_OPEN: 'funnel_enquiry_open',
  ENQUIRY_SUBMIT: 'funnel_enquiry_submit',
  LEAD_CONFIRMED: 'funnel_lead_confirmed',
};

// ─── USER JOURNEY EVENTS ─────────────────────────────────────────────────────
export const JOURNEY_EVENTS = {
  SEARCH_INITIATED: 'journey_search_initiated',
  SEARCH_RESULT_CLICK: 'journey_search_result_click',
  CATEGORY_SELECTED: 'journey_category_selected',
  SUBCATEGORY_SELECTED: 'journey_subcategory_selected',
  SERVICE_DETAIL_VIEWED: 'journey_service_detail_viewed',
  RELATED_SERVICE_CLICK: 'journey_related_service_click',
  BACK_NAVIGATION: 'journey_back_navigation',
  CTA_CLICK: 'journey_cta_click',
  PARTNER_REGISTRATION_START: 'journey_partner_registration_start',
  PARTNER_REGISTRATION_COMPLETE: 'journey_partner_registration_complete',
  CONSUMER_SIGNIN_START: 'journey_consumer_signin_start',
  CONSUMER_SIGNIN_COMPLETE: 'journey_consumer_signin_complete',
};

// ─── MARKETPLACE PERFORMANCE METRICS ─────────────────────────────────────────
export const MARKETPLACE_METRICS = {
  SERVICE_IMPRESSION: 'marketplace_service_impression',
  SERVICE_CLICK_RATE: 'marketplace_service_click',
  CATEGORY_ENGAGEMENT: 'marketplace_category_engagement',
  SEARCH_CONVERSION: 'marketplace_search_conversion',
  LEAD_QUALITY: 'marketplace_lead_quality',
  PARTNER_LISTING_VIEW: 'marketplace_partner_listing_view',
  FEATURED_SERVICE_CLICK: 'marketplace_featured_service_click',
  FILTER_APPLIED: 'marketplace_filter_applied',
  SORT_APPLIED: 'marketplace_sort_applied',
  COMPARISON_USED: 'marketplace_comparison_used',
};

// ─── CONVERSION FUNNEL TRACKING ───────────────────────────────────────────────

/**
 * Track a conversion funnel stage
 * @param {string} stage - FUNNEL_STAGES value
 * @param {object} params - Additional parameters
 */
export const trackFunnelStage = (stage, params = {}) => {
  trackEvent(stage, {
    funnel_name: 'lead_conversion',
    timestamp: new Date()?.toISOString(),
    ...params,
  });
};

/**
 * Track home page visit (Funnel Stage 1)
 */
export const trackHomeVisit = (params = {}) => {
  trackFunnelStage(FUNNEL_STAGES?.HOME_VISIT, {
    page_section: 'homepage',
    ...params,
  });
};

/**
 * Track category browse (Funnel Stage 2)
 */
export const trackCategoryBrowse = (categoryName, categoryId, params = {}) => {
  trackFunnelStage(FUNNEL_STAGES?.CATEGORY_BROWSE, {
    category_name: categoryName,
    category_id: categoryId,
    ...params,
  });
};

/**
 * Track service detail view (Funnel Stage 3)
 */
export const trackServiceView = (serviceName, serviceId, categoryName, params = {}) => {
  trackFunnelStage(FUNNEL_STAGES?.SERVICE_VIEW, {
    service_name: serviceName,
    service_id: serviceId,
    category_name: categoryName,
    ...params,
  });
};

/**
 * Track enquiry modal open (Funnel Stage 4)
 */
export const trackEnquiryOpen = (serviceName, serviceId, params = {}) => {
  trackFunnelStage(FUNNEL_STAGES?.ENQUIRY_OPEN, {
    service_name: serviceName,
    service_id: serviceId,
    ...params,
  });
};

/**
 * Track enquiry form submission (Funnel Stage 5)
 */
export const trackEnquirySubmit = (serviceName, serviceId, formData = {}, params = {}) => {
  trackFunnelStage(FUNNEL_STAGES?.ENQUIRY_SUBMIT, {
    service_name: serviceName,
    service_id: serviceId,
    has_company: !!formData?.company,
    budget_range: formData?.budget || formData?.budgetRange || 'not_specified',
    urgency: formData?.urgency || 'not_specified',
    ...params,
  });
};

/**
 * Track lead confirmed / saved to DB (Funnel Stage 6)
 */
export const trackLeadConfirmed = (serviceName, source, params = {}) => {
  trackFunnelStage(FUNNEL_STAGES?.LEAD_CONFIRMED, {
    service_name: serviceName,
    lead_source: source,
    ...params,
  });
};

// ─── USER JOURNEY TRACKING ────────────────────────────────────────────────────

/**
 * Track search initiation
 */
export const trackSearchInitiated = (query, source, params = {}) => {
  trackEvent(JOURNEY_EVENTS?.SEARCH_INITIATED, {
    search_query: query,
    search_source: source,
    query_length: query?.length || 0,
    ...params,
  });
};

/**
 * Track search result click
 */
export const trackSearchResultClick = (query, resultName, resultPosition, params = {}) => {
  trackEvent(JOURNEY_EVENTS?.SEARCH_RESULT_CLICK, {
    search_query: query,
    result_name: resultName,
    result_position: resultPosition,
    ...params,
  });
};

/**
 * Track category selection in journey
 */
export const trackCategorySelected = (categoryName, categoryId, source, params = {}) => {
  trackEvent(JOURNEY_EVENTS?.CATEGORY_SELECTED, {
    category_name: categoryName,
    category_id: categoryId,
    selection_source: source,
    ...params,
  });
};

/**
 * Track subcategory selection
 */
export const trackSubcategorySelected = (subcategoryName, subcategoryId, categoryName, params = {}) => {
  trackEvent(JOURNEY_EVENTS?.SUBCATEGORY_SELECTED, {
    subcategory_name: subcategoryName,
    subcategory_id: subcategoryId,
    parent_category: categoryName,
    ...params,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaLabel, ctaLocation, params = {}) => {
  trackEvent(JOURNEY_EVENTS?.CTA_CLICK, {
    cta_label: ctaLabel,
    cta_location: ctaLocation,
    ...params,
  });
};

/**
 * Track partner registration journey
 */
export const trackPartnerRegistrationStart = (params = {}) => {
  trackEvent(JOURNEY_EVENTS?.PARTNER_REGISTRATION_START, {
    journey_step: 'registration_start',
    ...params,
  });
};

export const trackPartnerRegistrationComplete = (params = {}) => {
  trackEvent(JOURNEY_EVENTS?.PARTNER_REGISTRATION_COMPLETE, {
    journey_step: 'registration_complete',
    ...params,
  });
};

// ─── MARKETPLACE PERFORMANCE METRICS ─────────────────────────────────────────

/**
 * Track service impressions (when services are rendered in a list)
 */
export const trackServiceImpressions = (services = [], listType, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.SERVICE_IMPRESSION, {
    list_type: listType,
    impression_count: services?.length || 0,
    service_ids: services?.slice(0, 10)?.map((s) => s?.id)?.join(','),
    ...params,
  });
};

/**
 * Track featured service click
 */
export const trackFeaturedServiceClick = (serviceName, serviceId, position, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.FEATURED_SERVICE_CLICK, {
    service_name: serviceName,
    service_id: serviceId,
    list_position: position,
    ...params,
  });
};

/**
 * Track category engagement (time spent / interactions)
 */
export const trackCategoryEngagement = (categoryName, categoryId, action, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.CATEGORY_ENGAGEMENT, {
    category_name: categoryName,
    category_id: categoryId,
    engagement_action: action,
    ...params,
  });
};

/**
 * Track filter usage
 */
export const trackFilterApplied = (filterType, filterValue, resultCount, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.FILTER_APPLIED, {
    filter_type: filterType,
    filter_value: filterValue,
    result_count: resultCount,
    ...params,
  });
};

/**
 * Track sort usage
 */
export const trackSortApplied = (sortBy, sortOrder, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.SORT_APPLIED, {
    sort_by: sortBy,
    sort_order: sortOrder,
    ...params,
  });
};

/**
 * Track partner listing view
 */
export const trackPartnerListingView = (partnerName, partnerId, serviceName, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.PARTNER_LISTING_VIEW, {
    partner_name: partnerName,
    partner_id: partnerId,
    service_name: serviceName,
    ...params,
  });
};

/**
 * Track comparison tool usage
 */
export const trackComparisonUsed = (serviceCount, serviceNames, params = {}) => {
  trackEvent(MARKETPLACE_METRICS?.COMPARISON_USED, {
    services_compared: serviceCount,
    service_names: serviceNames,
    ...params,
  });
};

// ─── ECOMMERCE / LEAD VALUE TRACKING ─────────────────────────────────────────

/**
 * Track lead as a GA4 purchase/conversion event for funnel reporting
 */
export const trackLeadAsConversion = (serviceName, budgetRange, source, params = {}) => {
  trackEvent('generate_lead', {
    currency: 'INR',
    value: estimateBudgetValue(budgetRange),
    service_name: serviceName,
    lead_source: source,
    ...params,
  });
};

/**
 * Estimate numeric value from budget range string for GA4 value tracking
 */
const estimateBudgetValue = (budgetRange) => {
  if (!budgetRange) return 0;
  const lower = budgetRange?.toLowerCase();
  if (lower?.includes('50 lakh') || lower?.includes('50l')) return 5000000;
  if (lower?.includes('25') && lower?.includes('50')) return 3750000;
  if (lower?.includes('10') && lower?.includes('25')) return 1750000;
  if (lower?.includes('5') && lower?.includes('10')) return 750000;
  if (lower?.includes('under') || lower?.includes('5 lakh')) return 250000;
  return 100000;
};

export default {
  trackFunnelStage,
  trackHomeVisit,
  trackCategoryBrowse,
  trackServiceView,
  trackEnquiryOpen,
  trackEnquirySubmit,
  trackLeadConfirmed,
  trackSearchInitiated,
  trackSearchResultClick,
  trackCategorySelected,
  trackSubcategorySelected,
  trackCTAClick,
  trackPartnerRegistrationStart,
  trackPartnerRegistrationComplete,
  trackServiceImpressions,
  trackFeaturedServiceClick,
  trackCategoryEngagement,
  trackFilterApplied,
  trackSortApplied,
  trackPartnerListingView,
  trackComparisonUsed,
  trackLeadAsConversion,
};
